import { Box, Card, Stack, Typography, useMediaQuery } from '@mui/material'
import type { MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { buildSocialOrderCommentNotificationUrl } from '../../../utils/commentNotificationLink'
import { PureLightTheme } from '../../../styles/theme'
import type {
  NotificationActorPayload,
  SocialOrderCommentNotificationPayload,
  SocialOrderCommentNotificationResource,
} from '../../../api/notifications'
import type { IProjectAuthorShort } from '../../../api/types/projects'
import {
  mediaSourceUrl,
  notificationActorAvatarSrc,
  notificationActorDisplayName,
  notificationActorPathKey,
  resolveNotificationActor,
} from './utils'
import { UserAvatar } from '../../common/UserAvatar'
import { NotificationEntityHeader } from './NotificationEntityHeader'
import { IconSocialOrders } from '../../../icons/IconSocialOrders'
import { StackedAvatars } from '../../ui-kit'
import { createNotificationNavHandlers, getSocialOrderSlug } from './notificationNav'
import { ActorNameSpan } from './notificationTypography'

type Props = {
  notification: SocialOrderCommentNotificationResource
}

function headerSubtitle(type: SocialOrderCommentNotificationResource['type']): string {
  switch (type) {
    case 'social_order.comment.created':
      return 'Новый комментарий'
    case 'social_order.comment.replied':
      return 'Ответ в обсуждении'
    case 'social_order.comment.liked':
      return 'Лайк комментария'
    default:
      return 'Комментарий'
  }
}

function actionLineWithActorLink(
  type: SocialOrderCommentNotificationResource['type'],
  actorName: string,
  onUserClick: (e: MouseEvent, pathKey: string) => void,
  actorPathKey: string,
) {
  const suffix =
    type === 'social_order.comment.created'
      ? ' оставил комментарий в обсуждении'
      : type === 'social_order.comment.replied'
        ? ' ответил вам в обсуждении'
        : type === 'social_order.comment.liked'
          ? ' поставил лайк вашему комментарию'
          : ' — действие в обсуждении'

  return (
    <Typography variant="black-14-normal" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
      <ActorNameSpan pathKey={actorPathKey} onUserClick={onUserClick}>
        {actorName}
      </ActorNameSpan>
      {suffix}
    </Typography>
  )
}

function likeStackUsers(
  socialOrder: SocialOrderCommentNotificationPayload['social_order'],
  socialOrderTitle: string,
  socialOrderAvatar: string | undefined,
  actor: NotificationActorPayload,
  actorName: string,
  actorAvatar: string | undefined,
): IProjectAuthorShort[] {
  const soId = socialOrder && typeof socialOrder.id === 'number' ? socialOrder.id : null
  const aid = typeof actor.id === 'number' && actor.id > 0 ? actor.id : null
  return [
    {
      id: soId,
      slug: socialOrder && typeof socialOrder.slug === 'string' ? socialOrder.slug : null,
      fullname: socialOrderTitle,
      avatar: socialOrderAvatar ?? null,
    },
    {
      id: aid,
      slug: actor.slug ?? null,
      fullname: actorName,
      avatar: actorAvatar ?? null,
      deleted: actor.deleted,
    },
  ]
}

export function SocialOrderCommentNotificationCard({ notification }: Props) {
  const navigate = useNavigate()
  const { onSocialOrderClick, onUserClick } = createNotificationNavHandlers(navigate)
  const isMobile = useMediaQuery(PureLightTheme.breakpoints.down('md'))
  const payload = notification.payload
  const socialOrder = payload.social_order ?? null
  const actor = resolveNotificationActor(payload, notification.actor_user_id)
  const socialOrderTitle =
    socialOrder && typeof socialOrder.title === 'string' && socialOrder.title.trim().length > 0
      ? socialOrder.title
      : 'Соцзаказ'
  const actorName = notificationActorDisplayName(actor)
  const socialOrderAvatarSrc = mediaSourceUrl(socialOrder?.image_url) ?? undefined
  const actorAvatarSrc = notificationActorAvatarSrc(actor)
  const preview =
    typeof payload.preview === 'string' && payload.preview.trim().length > 0 ? payload.preview.trim() : null

  const socialOrderSlug = getSocialOrderSlug(
    socialOrder,
    notification.subject_kind === 'social_order' ? notification.subject_id : null,
  )
  const actorPathKey = notificationActorPathKey(actor, notification.actor_user_id)

  const navigateToComment = () => {
    if (!socialOrderSlug) {
      toast.error('Соцзаказ недоступен')
      return
    }
    navigate(buildSocialOrderCommentNotificationUrl(socialOrderSlug, payload, { isMobile }))
  }

  const previewSx = { opacity: 0.9, wordBreak: 'break-word' as const }

  return (
    <Card variant="primaryAdaptiveCard" sx={{ p: 2, cursor: 'pointer' }} onClick={navigateToComment}>
      <Stack direction="column" minWidth={0} flex={1} gap={1.25}>
        <Box
          onClick={(e) => onSocialOrderClick(e, socialOrderSlug)}
          sx={{ cursor: socialOrderSlug ? 'pointer' : 'default', alignSelf: 'stretch' }}
        >
          <NotificationEntityHeader
            title={socialOrderTitle}
            avatarSrc={socialOrderAvatarSrc}
            fallbackIcon={<IconSocialOrders fontSize="small" sx={{ color: 'inherit' }} />}
            subtitle={headerSubtitle(notification.type)}
          />
        </Box>

        {notification.type === 'social_order.comment.liked' ? (
          <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
            <StackedAvatars
              users={likeStackUsers(
                socialOrder,
                socialOrderTitle,
                socialOrderAvatarSrc,
                actor,
                actorName,
                actorAvatarSrc,
              )}
              size={34}
              overlap={8}
            />
            {actionLineWithActorLink(notification.type, actorName, onUserClick, actorPathKey)}
          </Stack>
        ) : (
          <Stack direction="row" gap={1.5} alignItems="flex-start" minWidth={0}>
            <Box
              onClick={actorPathKey ? (e) => onUserClick(e, actorPathKey) : undefined}
              sx={{ cursor: actorPathKey ? 'pointer' : 'default', flexShrink: 0 }}
            >
              <UserAvatar src={actorAvatarSrc ?? null} size={34} />
            </Box>
            <Stack minWidth={0} gap={0.5} flex={1}>
              {actionLineWithActorLink(notification.type, actorName, onUserClick, actorPathKey)}
              {preview && (
                <Typography variant="black-12-light" sx={previewSx}>
                  «{preview}»
                </Typography>
              )}
            </Stack>
          </Stack>
        )}

        {notification.type === 'social_order.comment.liked' && preview && (
          <Typography variant="black-12-light" sx={previewSx}>
            «{preview}»
          </Typography>
        )}

        {typeof notification.message === 'string' && notification.message.trim().length > 0 && (
          <Typography variant="black-12-light" sx={{ opacity: 0.85 }}>
            {notification.message}
          </Typography>
        )}

        <Typography variant="black-12-light" noWrap sx={{ opacity: 0.8 }}>
          {notification.read_at ? 'Прочитано' : 'Непрочитано'} • {new Date(notification.created_at).toLocaleString()}
        </Typography>
      </Stack>
    </Card>
  )
}
